import { searchClient } from "@/features/search/api/client";
import type { components } from "@/features/search/api/schema";
import { toLang } from "@/features/search/utils/search-params.utils";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";

export type SavedSearch = components["schemas"]["SavedSearch"];
export type SavedSearchFilter = components["schemas"]["SavedSearchFilter"];
export type CreateSavedSearchBody = components["schemas"]["CreateSavedSearch"];
export type PatchSavedSearchBody = components["schemas"]["PatchSavedSearch"];

/**
 * Cache root for saved searches. Every mutation invalidates it wholesale — one page is small and
 * always refetched with the viewer's own token, so there is nothing to gain from patching
 * individual entries.
 */
export const SAVED_SEARCHES_QUERY_KEY = ["saved-searches"] as const;

/**
 * The service's own default page size, sent explicitly so the number lives here rather than in
 * whatever the service defaults to. Do not raise it: each row on a page costs two Typesense
 * sub-searches to compute its match count, so page size multiplies backend work directly
 * (WHO-5740).
 */
export const SAVED_SEARCHES_PAGE_SIZE = 25;

/**
 * Unlike `/search`, these endpoints are not anonymous: every one of them is scoped to the bearer
 * token's member and answers 401 without it. So the token is a required argument here rather than
 * an optional personalisation, and callers gate on it (see `enabled` below).
 */
const authHeaders = (token: string) => ({ Authorization: `Bearer ${token}` });

/**
 * The caller's own saved searches, newest first.
 *
 * Keyed on the viewer, which `searchRouteQueryOptions` deliberately avoids — that key has to
 * survive SSR hydration, this one never renders on the server (the page is behind auth) and must
 * not leak one member's list into another's cache after a re-login.
 */
export const savedSearchesQueryOptions = (
  token: string | null | undefined,
  lang?: string,
  page = 1
) =>
  queryOptions({
    queryKey: [...SAVED_SEARCHES_QUERY_KEY, { token, lang, page }] as const,
    enabled: Boolean(token),
    /* Keeps the current page on screen while the next one loads, so the pager stays put instead of
     * collapsing into the loading skeleton on every page change. */
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const { data, error } = await searchClient.GET("/saved-searches", {
        params: {
          query: { page, limit: SAVED_SEARCHES_PAGE_SIZE, lang: toLang(lang) },
        },
        headers: authHeaders(token as string),
        signal,
      });

      if (error) {
        throw error;
      }

      return data;
    },
  });

/**
 * Save the current search.
 *
 * `params` is the canonical `/search` query string — literally what the address bar already holds
 * (`location.search.slice(1)`). The service drops unknown params and view state (sort, page, lang),
 * so there is no filter mapping on this side: the thing we send is the thing the user is looking at.
 *
 * Creating is idempotent — saving a search the member already has returns the existing one with
 * `200` rather than a duplicate, so the button needs no "already saved?" lookup of its own.
 */
export const createSavedSearch = async (
  token: string,
  body: CreateSavedSearchBody
) => {
  const { data, error } = await searchClient.POST("/saved-searches", {
    body,
    headers: authHeaders(token),
  });

  if (error) {
    throw error;
  }

  return data;
};

export const patchSavedSearch = async (
  token: string,
  id: string,
  body: PatchSavedSearchBody
) => {
  const { data, error } = await searchClient.PATCH("/saved-searches/{id}", {
    params: { path: { id } },
    body,
    headers: authHeaders(token),
  });

  if (error) {
    throw error;
  }

  return data;
};

export const deleteSavedSearch = async (token: string, id: string) => {
  const { error } = await searchClient.DELETE("/saved-searches/{id}", {
    params: { path: { id } },
    headers: authHeaders(token),
  });

  if (error) {
    throw error;
  }
};
